import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useScope } from "@/hooks/use-scope";
import { runServerFn } from "@/lib/server-fn";
import { getWaveDetail, getWaveProgress, listWaveSignals } from "@/lib/wave-policies.functions";
import {
  releaseWave, cancelWave, removeOrderFromWave,
} from "@/lib/waves.functions";
import {
  listOrderOperationalSummaries,
  type OrderSummaryLite,
} from "@/lib/orders/summary.functions";
import { OrderRef } from "@/components/orders/order-ref";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  ArrowLeft, Play, Ban, ShieldAlert, CheckCircle2, XCircle, MinusCircle, Activity, Bell,
} from "lucide-react";
import { toast } from "sonner";
import { WavePolicyPanel } from "@/components/waves/wave-policy-panel";

export const Route = createFileRoute("/_authenticated/app/pick/waves/$waveId")({
  component: WaveDetail,
});

function WaveDetail() {
  const { waveId } = Route.useParams();
  const { tenantId } = useScope();
  const detailFn = useServerFn(getWaveDetail);
  const releaseFn = useServerFn(releaseWave);
  const cancelFn = useServerFn(cancelWave);
  const removeFn = useServerFn(removeOrderFromWave);
  const qc = useQueryClient();
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [busy, setBusy] = useState(false);

  const q = useQuery({
    queryKey: ["wave-detail", tenantId, waveId],
    enabled: !!tenantId,
    queryFn: () => detailFn({ data: { tenantId: tenantId!, waveId } }),
  });

  const progressFn = useServerFn(getWaveProgress);
  const signalsFn = useServerFn(listWaveSignals);
  const pq = useQuery({
    queryKey: ["wave-progress", tenantId, waveId],
    enabled: !!tenantId,
    queryFn: () => progressFn({ data: { tenantId: tenantId!, waveId } }),
    refetchInterval: 30_000,
  });
  const sq = useQuery({
    queryKey: ["wave-signals", tenantId, waveId],
    enabled: !!tenantId,
    queryFn: () => signalsFn({ data: { tenantId: tenantId!, waveId } }),
    refetchInterval: 30_000,
  });

  // Sprint 36 closeout — hydrate OrderSummaryLite so the wave membership
  // table can render <OrderRef> instead of a raw UUID slice.
  const summariesFn = useServerFn(listOrderOperationalSummaries);
  const orderIds = (q.data?.orders as any[] | undefined)?.map((o: any) => o.order_id) ?? [];
  const osq = useQuery({
    queryKey: ["wave-order-summaries", orderIds],
    enabled: orderIds.length > 0,
    queryFn: () => summariesFn({ data: { orderIds } }),
  });
  const orderSummaryById = new Map<string, OrderSummaryLite>();
  for (const s of ((osq.data as OrderSummaryLite[] | undefined) ?? [])) orderSummaryById.set(s.identity.id, s);

  if (q.isLoading) return <div className="p-6 text-sm text-muted-foreground">Loading wave…</div>;
  if (q.error) return <div className="p-6 text-sm text-destructive">{(q.error as Error).message}</div>;
  const wave = q.data!.wave as any;
  const orders = q.data!.orders as any[];
  const policy = q.data!.policy as any;
  const summaries = q.data!.orderSummaries as Record<string, any>;

  const canRelease = wave.status === "planned" || wave.status === "draft";
  const canCancel = canRelease;
  const blocked = wave.released_explanation?.blocked_gates?.length > 0;

  const doRelease = async () => {
    setBusy(true);
    try {
      const r = await runServerFn(
        () => releaseFn({ data: { tenantId: tenantId!, waveId } }),
        "Releasing wave",
      );
      if (r.released) toast.success(`Wave released — ${r.pickJobIds?.length ?? 0} pick job(s) created`);
      else toast.error("Release blocked — see explainability below");
      qc.invalidateQueries({ queryKey: ["wave-detail", tenantId, waveId] });
    } finally { setBusy(false); }
  };
  const doCancel = async () => {
    if (!cancelReason.trim()) { toast.error("Reason required"); return; }
    setBusy(true);
    try {
      await runServerFn(
        () => cancelFn({ data: { tenantId: tenantId!, waveId, reason: cancelReason.trim() } }),
        "Cancelling wave",
      );
      toast.success("Wave cancelled");
      setCancelOpen(false);
      qc.invalidateQueries({ queryKey: ["wave-detail", tenantId, waveId] });
    } finally { setBusy(false); }
  };
  const doRemove = async (orderId: string) => {
    const reason = prompt("Reason for removing this order (audited)") ?? "";
    if (!reason.trim()) return;
    await runServerFn(
      () => removeFn({ data: { tenantId: tenantId!, waveId, orderId, reason: reason.trim() } }),
      "Removing order",
    );
    qc.invalidateQueries({ queryKey: ["wave-detail", tenantId, waveId] });
  };

  return (
    <div className="p-4 sm:p-6 space-y-4 max-w-6xl">
      <div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <Link to="/app/pick/waves" className="inline-flex items-center gap-1 hover:text-foreground">
            <ArrowLeft className="h-3 w-3" /> Waves
          </Link>
          <span>·</span>
          <Link to="/app/operations/desk" className="inline-flex items-center gap-1 hover:text-foreground">
            Operations Workspace
          </Link>
        </div>
        <div className="flex items-start justify-between gap-4 mt-1">
          <div>
            <h1 className="text-xl font-semibold">{wave.name}</h1>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="default" className="capitalize">{String(wave.status).replace("_", " ")}</Badge>
              {policy && <span className="text-xs text-muted-foreground">Policy: {policy.name}</span>}
              {blocked && (
                <span className="text-xs text-destructive inline-flex items-center gap-1">
                  <ShieldAlert className="h-3 w-3" /> Release blocked
                </span>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            {canCancel && (
              <Button variant="outline" onClick={() => setCancelOpen(true)} disabled={busy}>
                <Ban className="h-4 w-4" /> Cancel
              </Button>
            )}
            {canRelease && (
              <Button onClick={doRelease} disabled={busy}>
                <Play className="h-4 w-4" /> Release
              </Button>
            )}
          </div>
        </div>
      </div>

      <ExplanationCard
        title="Plan"
        explanation={wave.created_explanation}
        icon={<CheckCircle2 className="h-4 w-4 text-success" />}
      />
      <WavePolicyPanel tenantId={tenantId!} waveId={waveId} />
      {pq.data && (wave.status === "released" || wave.status === "in_progress" || wave.status === "completed") && (
        <ProgressCard data={pq.data} />
      )}
      {sq.data && (sq.data.signals?.length ?? 0) > 0 && (
        <WaveSignalsCard signals={sq.data.signals as any[]} />
      )}
      {wave.released_explanation && (
        <ExplanationCard
          title={blocked ? "Release blocked" : "Released"}
          explanation={wave.released_explanation}
          icon={blocked
            ? <ShieldAlert className="h-4 w-4 text-destructive" />
            : <Play className="h-4 w-4 text-success" />}
        />
      )}
      {wave.cancelled_explanation && (
        <ExplanationCard
          title="Cancelled"
          explanation={wave.cancelled_explanation}
          icon={<Ban className="h-4 w-4 text-muted-foreground" />}
        />
      )}

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Orders in wave ({orders.length})</CardTitle>
          <CardDescription>
            Every inclusion or exclusion has an explanation. Operator overrides are audited.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[80px]">Rank</TableHead>
                <TableHead>Order</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>State</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead className="w-[100px] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orders.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No orders in this wave.</TableCell></TableRow>
              ) : orders.map((o) => {
                const s = summaries[o.order_id] ?? {};
                const exp = o.removed_explanation ?? o.included_explanation;
                return (
                  <TableRow key={o.id}>
                    <TableCell className="tabular-nums">{o.priority_rank}</TableCell>
                    <TableCell>
                      <OrderRef
                        summary={orderSummaryById.get(o.order_id)}
                        orderId={o.order_id}
                        fallbackOrderNumber={s.order_number ?? "—"}
                        className="font-mono text-xs"
                      />
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground capitalize">
                      {s.status ?? "—"}
                    </TableCell>
                    <TableCell>
                      <Badge variant={o.state === "removed" ? "outline" : "secondary"} className="capitalize">
                        {o.state}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-[300px]">
                      <div className="line-clamp-2">{exp?.headline ?? "—"}</div>
                    </TableCell>
                    <TableCell className="text-right">
                      {(wave.status === "planned" || wave.status === "draft") && o.state === "planned" && (
                        <Button variant="ghost" size="sm" onClick={() => doRemove(o.order_id)}>
                          <MinusCircle className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Cancel wave</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              Cancellation is audited. Once released, you'd cancel the underlying pick jobs instead.
            </p>
            <Textarea
              placeholder="Reason for cancelling…"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCancelOpen(false)}>Back</Button>
            <Button variant="destructive" onClick={doCancel} disabled={busy}>Cancel wave</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ExplanationCard({
  title, explanation, icon,
}: { title: string; explanation: any; icon: React.ReactNode }) {
  if (!explanation) return null;
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          {icon} {title}
        </CardTitle>
        <CardDescription>{explanation.headline}</CardDescription>
      </CardHeader>
      <CardContent className="pt-0 text-sm text-muted-foreground space-y-1">
        {(explanation.detail ?? []).map((d: string, i: number) => (
          <div key={i}>• {d}</div>
        ))}
        {explanation.blocked_gates && explanation.blocked_gates.length > 0 && (
          <div className="mt-2 space-y-1">
            {explanation.blocked_gates.map((g: any) => (
              <div key={g.key} className="flex items-start gap-2 text-destructive">
                <XCircle className="h-3.5 w-3.5 mt-0.5" />
                <div>
                  <div className="font-medium text-xs">{g.key}</div>
                  <div className="text-xs">{g.detail}</div>
                </div>
              </div>
            ))}
          </div>
        )}
        {explanation.source && (
          <div className="text-xs pt-1">Source: {explanation.source}</div>
        )}
      </CardContent>
    </Card>
  );
}

function ProgressCard({ data }: { data: any }) {
  const pct = data.percentComplete ?? 0;
  const cutoffMs = data.windowEnd ? new Date(data.windowEnd).getTime() - Date.now() : null;
  const cutoffMins = cutoffMs !== null ? Math.round(cutoffMs / 60000) : null;
  const atRisk = cutoffMins !== null && cutoffMins < 30 && pct < 100;
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Activity className="h-4 w-4" /> Progress
        </CardTitle>
        <CardDescription>
          {data.jobCount} pick job(s) · {data.assignedPickers.length} picker(s) assigned ·{" "}
          {data.sessions.active} active session(s)
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <div className="flex justify-between text-xs text-muted-foreground mb-1">
            <span>{pct}% complete</span>
            <span className="tabular-nums">
              {data.jobs.completed}/{data.jobCount} jobs done
            </span>
          </div>
          <Progress value={pct} />
        </div>
        <div className="flex flex-wrap gap-2 text-xs">
          <Badge variant="secondary">{data.jobs.pending} pending</Badge>
          <Badge variant="default">{data.jobs.in_progress} in progress</Badge>
          <Badge variant="outline">{data.jobs.completed} completed</Badge>
          {data.jobs.cancelled > 0 && <Badge variant="outline">{data.jobs.cancelled} cancelled</Badge>}
          {cutoffMins !== null && (
            <Badge variant={atRisk ? "destructive" : "outline"} className="ml-auto">
              {cutoffMins > 0 ? `${cutoffMins}m to cutoff` : `${Math.abs(cutoffMins)}m past cutoff`}
            </Badge>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function WaveSignalsCard({ signals }: { signals: any[] }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Bell className="h-4 w-4" /> Open signals ({signals.length})
        </CardTitle>
        <CardDescription>
          These also appear in Operations Workspace. Resolve them here or there — same record.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        {signals.map((s) => {
          const exp = s.payload?.explanation;
          const variant =
            s.severity === "critical" || s.severity === "error"
              ? "destructive"
              : s.severity === "warn"
                ? "default"
                : "secondary";
          return (
            <div key={s.id} className="border rounded-md p-3">
              <div className="flex items-center justify-between gap-2">
                <div className="text-sm font-medium">{exp?.headline ?? s.signal_type}</div>
                <Badge variant={variant as any} className="capitalize">{s.severity}</Badge>
              </div>
              {exp?.detail?.length > 0 && (
                <div className="mt-1 text-xs text-muted-foreground space-y-0.5">
                  {exp.detail.map((d: string, i: number) => <div key={i}>• {d}</div>)}
                </div>
              )}
              <div className="mt-1 text-[11px] text-muted-foreground">
                Reason: {s.payload?.reason_code ?? "—"} ·{" "}
                {new Date(s.occurred_at).toLocaleString()}
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}